import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import useUpdateRegtankOnboardingReattemptApproval from "../../../../hooks/useUpdateRegtankOnboardingReattemptApproval";
import useUpdateRegtankLivenessReattemptApproval from "../../../../hooks/useUpdateRegtankLivenessReattemptApproval";
import {
  changeStatusAfterReattemptApproval,
  changeStatusLivenessReattemptApproval,
} from "../../../../Utils/getRegtankDetails";
import {
  RegtankOnboardingFHStatusEnum,
  RegtankLivenessFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import type {
  RegtankData,
  RegtankHistoryData,
  RegtankLivenessData,
  RegtankLivenessHistoryData,
} from "../Regtank.types";
import type { Data, UserData } from "../../../../types/org.type";

interface UseReattemptApprovalProps {
  token: string;
  orgData: Data | undefined;
  userData: UserData[] | undefined;
  onCloseApprovalModal: () => void;
  onCloseRejectionModal: () => void;
  convertToUtcTwelveHours: (date: any) => string;
  verificationHistoryData: RegtankHistoryData[];
  setVerificationHistoryData: (data: RegtankHistoryData[]) => void;
  livenessHistoryData: RegtankLivenessHistoryData[];
  setLivenessHistoryData: (data: RegtankLivenessHistoryData[]) => void;
  setRegtankOnboardingPerUserRowData: (
    updater: (prev: RegtankData | undefined) => RegtankData | undefined
  ) => void;
  setRegtankLivenessCardData: (
    updater: (
      prev: RegtankLivenessData | undefined
    ) => RegtankLivenessData | undefined
  ) => void;
  setFinalData: (updater: (prev: RegtankData[]) => RegtankData[]) => void;
  setUpdatedFinalData: (
    updater: (prev: RegtankData[]) => RegtankData[]
  ) => void;
  setLivenessFinalData: (
    updater: (prev: RegtankLivenessData[]) => RegtankLivenessData[]
  ) => void;
  setUpdatedLivenessFinalData: (
    updater: (prev: RegtankLivenessData[]) => RegtankLivenessData[]
  ) => void;
  finalData: RegtankData[];
  livenessFinalData: RegtankLivenessData[];
}

/**
 * Custom hook to handle reattempt approval/rejection for both onboarding and liveness
 * Consolidates duplicate approval logic
 */
export const useReattemptApproval = ({
  token,
  orgData,
  userData,
  onCloseApprovalModal,
  onCloseRejectionModal,
  convertToUtcTwelveHours,
  verificationHistoryData,
  setVerificationHistoryData,
  livenessHistoryData,
  setLivenessHistoryData,
  setRegtankOnboardingPerUserRowData,
  setRegtankLivenessCardData,
  setFinalData,
  setUpdatedFinalData,
  setLivenessFinalData,
  setUpdatedLivenessFinalData,
  finalData,
  livenessFinalData,
}: UseReattemptApprovalProps) => {
  const [remark, setRemark] = useState("");
  const [remarkApprButtonDisabled, setRemarkApprButtonDisabled] =
    useState(true);
  const [remarkRejectButtonDisabled, setRemarkRejectButtonDisabled] =
    useState(true);
  const [nameRole, setNameRole] = useState("");
  const [userId, setUserId] = useState("");
  const [requestIdReattempt, setRequestIdReattempt] = useState("");
  const [sessionIdReattempt, setSessionIdReattempt] = useState("");

  const updateOnboardingReattemptApprovalMutation =
    useUpdateRegtankOnboardingReattemptApproval();
  const updateLivenessReattemptApprovalMutation =
    useUpdateRegtankLivenessReattemptApproval();

  // Enable/disable buttons based on remark input
  useEffect(() => {
    if (remark !== "" && !remark.startsWith(" ")) {
      setRemarkApprButtonDisabled(false);
      setRemarkRejectButtonDisabled(false);
    } else {
      setRemarkApprButtonDisabled(true);
      setRemarkRejectButtonDisabled(true);
    }
  }, [remark]);

  const handleOnboardingReattempt = useCallback(
    (isApproved: boolean) => {
      // Set updated data before processing
      setUpdatedFinalData(() => finalData);
      const reattemptRemark = remark;
      const reattemptRequestId = requestIdReattempt;
      const rejectOrApprove = isApproved ? "APPROVED" : "REJECTED";
      const reviewedOn = convertToUtcTwelveHours(new Date());
      const organization = {
        emailOfAuthorisedRepresentiative:
          orgData?.emailOfAuthorisedRepresentiative,
        boardOfDirectors: orgData?.boardOfDirectors,
      };
      let userDataCheck: any;
      if (userData !== undefined) {
        userDataCheck = userData.find((x) => x.id === Number(userId));
      }

      updateOnboardingReattemptApprovalMutation.mutate(
        {
          token,
          requestId: reattemptRequestId,
          remark: reattemptRemark,
          reattemptApproval: rejectOrApprove,
          userId: userId,
          userData: userDataCheck,
          organization,
        },
        {
          onSuccess: () => {
            const dataUpdated = verificationHistoryData.map((x) =>
              x.requestId === reattemptRequestId
                ? {
                    ...x,
                    remark: reattemptRemark,
                    reviewedOn: reviewedOn,
                    reattemptApproval: rejectOrApprove,
                  }
                : x
            );

            setVerificationHistoryData(dataUpdated);

            if (dataUpdated.length === 0) {
              if (isApproved) {
                onCloseApprovalModal();
              } else {
                onCloseRejectionModal();
              }
              return;
            }

            const firstItem = dataUpdated[0];
            const status: RegtankOnboardingFHStatusEnum =
              changeStatusAfterReattemptApproval({
                reattemptApproval: firstItem.reattemptApproval || "",
                verificationResult: firstItem.verificationResult,
              });

            const targetUserId = Number(firstItem.userId);

            setRegtankOnboardingPerUserRowData((prevData) => {
              if (!prevData) return undefined;
              return {
                ...prevData,
                onboardingStatus: status,
              };
            });

            setFinalData((prev) =>
              prev.map((x) =>
                x.userId === targetUserId
                  ? { ...x, onboardingStatus: status }
                  : x
              )
            );
            setUpdatedFinalData((prev) =>
              prev.map((x) =>
                x.userId === targetUserId
                  ? { ...x, onboardingStatus: status }
                  : x
              )
            );
            if (isApproved) {
              onCloseApprovalModal();
            } else {
              onCloseRejectionModal();
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
            if (isApproved) {
              onCloseApprovalModal();
            } else {
              onCloseRejectionModal();
            }
          },
        }
      );
    },
    [
      remark,
      requestIdReattempt,
      userId,
      token,
      orgData,
      userData,
      updateOnboardingReattemptApprovalMutation,
      convertToUtcTwelveHours,
      onCloseApprovalModal,
      onCloseRejectionModal,
      verificationHistoryData,
      setVerificationHistoryData,
      setRegtankOnboardingPerUserRowData,
      setFinalData,
      setUpdatedFinalData,
      finalData,
    ]
  );

  const handleLivenessReattempt = useCallback(
    (isApproved: boolean) => {
      // Set updated data before processing
      setUpdatedLivenessFinalData(() => livenessFinalData);
      const reattemptRemark = remark;
      const reattemptSessionId = sessionIdReattempt;
      const rejectOrApprove = isApproved ? "APPROVED" : "REJECTED";
      const reviewedOn = convertToUtcTwelveHours(new Date());
      let userDataCheck: any;
      if (userData !== undefined) {
        userDataCheck = userData.find((x) => x.id === Number(userId));
      }

      updateLivenessReattemptApprovalMutation.mutate(
        {
          token,
          sessionId: reattemptSessionId,
          remark: reattemptRemark,
          reattemptApproval: rejectOrApprove,
          userId: userId,
          userData: userDataCheck,
        },
        {
          onSuccess: () => {
            const dataUpdated = livenessHistoryData.map((x) =>
              x.sessionId === reattemptSessionId
                ? {
                    ...x,
                    remark: reattemptRemark,
                    reviewedOn: reviewedOn,
                    reattemptApproval: rejectOrApprove,
                  }
                : x
            );

            setLivenessHistoryData(dataUpdated);

            if (dataUpdated.length === 0) {
              if (isApproved) {
                onCloseApprovalModal();
              } else {
                onCloseRejectionModal();
              }
              return;
            }

            const firstItem = dataUpdated[0];
            const status: RegtankLivenessFHStatusEnum =
              changeStatusLivenessReattemptApproval({
                reattemptApproval: firstItem.reattemptApproval || "",
                verificationResult: firstItem.verificationResult,
              });

            const targetUserId = Number(firstItem.userId);

            setRegtankLivenessCardData((prevData) => {
              if (!prevData) return undefined;
              return {
                ...prevData,
                verificationStatus: status,
              };
            });

            setLivenessFinalData((prev) =>
              prev.map((x) =>
                x.userId === targetUserId
                  ? { ...x, verificationStatus: status }
                  : x
              )
            );
            setUpdatedLivenessFinalData((prev) =>
              prev.map((x) =>
                x.userId === targetUserId
                  ? { ...x, verificationStatus: status }
                  : x
              )
            );
            if (isApproved) {
              onCloseApprovalModal();
            } else {
              onCloseRejectionModal();
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
            if (isApproved) {
              onCloseApprovalModal();
            } else {
              onCloseRejectionModal();
            }
          },
        }
      );
    },
    [
      remark,
      sessionIdReattempt,
      userId,
      token,
      userData,
      updateLivenessReattemptApprovalMutation,
      convertToUtcTwelveHours,
      onCloseApprovalModal,
      onCloseRejectionModal,
      livenessHistoryData,
      setLivenessHistoryData,
      setRegtankLivenessCardData,
      setLivenessFinalData,
      setUpdatedLivenessFinalData,
      livenessFinalData,
    ]
  );

  // Pre-configured handlers for approval and rejection
  const handleOnboardingApproveReattempt = useCallback(() => {
    handleOnboardingReattempt(true);
  }, [handleOnboardingReattempt]);

  const handleOnboardingRejectReattempt = useCallback(() => {
    handleOnboardingReattempt(false);
  }, [handleOnboardingReattempt]);

  const handleLivenessApproveReattempt = useCallback(() => {
    handleLivenessReattempt(true);
  }, [handleLivenessReattempt]);

  const handleLivenessRejectReattempt = useCallback(() => {
    handleLivenessReattempt(false);
  }, [handleLivenessReattempt]);

  return {
    remark,
    setRemark,
    remarkApprButtonDisabled,
    remarkRejectButtonDisabled,
    nameRole,
    setNameRole,
    userId,
    setUserId,
    requestIdReattempt,
    setRequestIdReattempt,
    sessionIdReattempt,
    setSessionIdReattempt,
    handleOnboardingReattempt,
    handleLivenessReattempt,
    // Pre-configured handlers
    handleOnboardingApproveReattempt,
    handleOnboardingRejectReattempt,
    handleLivenessApproveReattempt,
    handleLivenessRejectReattempt,
  };
};
